import React, { useEffect, useState } from "react";
import classes from "./reports.module.css";
import classesOne from "../MyTeam/myteam.module.css";
import { useLocation } from "react-router-dom";
import {
  authApi,
  getAuthorizationHeader,
  getDownloadErrorMessage,
} from "../../../api-calls/user-requests";
import { toast } from "react-toastify";
import { VictoryPie } from "victory";
import { VictoryLabel } from "victory-core";
import dayjs from "dayjs";
import { StyledMenu } from "../../../components/styledMenu";
import { GoDotFill } from "react-icons/go";
import {
  Typography,
  Button,
  MenuItem,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Menu,
  Box,
  CircularProgress,
} from "@mui/material";
import { Checkbox, FormControlLabel } from "@mui/material";
import {
  ArrowDownIcon,
  ExcelIcon,
  FilterIcon,
  PdfIcon,
  ReportFilterIcon,
} from "../../../components/Icons";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useSelector } from "react-redux";
import moment from "moment";
function useQuery() {
  return new URLSearchParams(useLocation().search);
}

// Expense-type presentation. Colours match data/expenseReport.js on the backend
// so the on-screen report and the downloaded PDF read as the same document.
const EXPENSE_TYPE_META = [
  { key: "travelling", label: "Travelling", color: "#6366F1" },
  { key: "food", label: "Food", color: "#10B981" },
  { key: "accommodation", label: "Accommodation", color: "#F59E0B" },
  { key: "material", label: "Material", color: "#3B82F6" },
  { key: "misc", label: "Others", color: "#94A3B8" },
];

const formatAmount = (value) =>
  new Intl.NumberFormat("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

// Mirrors resolveImageUrl in data/taskReport.js — bills are stored as the same
// multer-S3 metadata object as task photos.
const resolveBillUrl = (bill) => {
  if (!bill) return "";
  if (typeof bill === "string") return /^https?:\/\//i.test(bill) ? bill : "";
  if (bill.location && /^https?:\/\//i.test(bill.location)) return bill.location;
  if (bill.destination && /^https?:\/\//i.test(bill.destination)) {
    if (!bill.filename) return bill.destination;
    return bill.destination.endsWith("/")
      ? `${bill.destination}${bill.filename}`
      : `${bill.destination}/${bill.filename}`;
  }
  return "";
};

/**
 * Expense report renderer.
 *
 * Kept separate from EmployeeReportSection, which is branched throughout on
 * task-vs-attendance; threading a third shape through every ternary there would
 * have made all three harder to read.
 */
const ExpenseReportSection = ({ report }) => {
  const [expanded, setExpanded] = React.useState("panel1");
  const [typeFilter, setTypeFilter] = useState("all");
  const [anchorElFilter, setAnchorElFilter] = React.useState(null);

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const byType = report?.by_type || {};
  const total = report?.total_amount || 0;

  const pieChartData = EXPENSE_TYPE_META.map((t) => ({
    x: t.label,
    y: byType[t.key]?.amount || 0,
  })).filter((d) => d.y > 0);

  const colorScale = EXPENSE_TYPE_META.filter(
    (t) => (byType[t.key]?.amount || 0) > 0
  ).map((t) => t.color);

  // Largest category by amount — the headline a leader scans for first.
  const largest = EXPENSE_TYPE_META.reduce(
    (best, t) =>
      (byType[t.key]?.amount || 0) > (byType[best.key]?.amount || 0) ? t : best,
    EXPENSE_TYPE_META[0]
  );

  const rows = (report?.list || []).filter(
    (item) => typeFilter === "all" || item.type === typeFilter
  );

  return (
    <div className={classes.AccordiansContainer}>
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <b>Expense Summary</b>
        </AccordionSummary>
        <AccordionDetails>
          <div className={classes.TextNumberCardContainer}>
            <div className={classes.TextNumberCard}>
              <Typography className={classes.textCardLable}>
                Total Claimed
              </Typography>
              <Typography className={classes.textCardLable}>
                ₹{formatAmount(total)}
              </Typography>
            </div>
            <div className={classes.TextNumberCard}>
              <Typography className={classes.textCardLable}>
                Expenses
              </Typography>
              <Typography className={classes.textCardLable}>
                {report?.count || 0}
              </Typography>
            </div>
            <div className={classes.TextNumberCard}>
              <Typography className={classes.textCardLable}>
                Largest Category
              </Typography>
              <Typography className={classes.textCardLable}>
                {total > 0 ? largest.label : "—"}
              </Typography>
            </div>
            <div className={classes.TextNumberCard}>
              <Typography className={classes.textCardLable}>
                Date Range
              </Typography>
              <Typography className={classes.textCardLable}>
                {report?.startDate
                  ? `${dayjs(report.startDate).format("DD MMM")} – ${dayjs(
                      report.endDate
                    ).format("DD MMM YY")}`
                  : "—"}
              </Typography>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === "panel2"}
        onChange={handleChange("panel2")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <b>Expense Distribution</b>
        </AccordionSummary>
        <AccordionDetails>
          <div className={classes.pieChartContainer}>
            <svg viewBox="0 0 450 450" width="170px">
              <VictoryPie
                standalone={false}
                width={450}
                height={450}
                data={pieChartData}
                innerRadius={125}
                colorScale={colorScale}
                labels={() => null}
                style={{ labels: { fontSize: 20, fill: "white" } }}
              />
              <VictoryLabel
                textAnchor="middle"
                verticalAnchor="middle"
                x={225}
                y={250}
                text={[`₹${formatAmount(total)}`, "Total"]}
                style={[
                  { fontSize: 45, fontWeight: 600 },
                  { fontSize: "30px", fontWeight: 400, fill: "#6B778C" },
                ]}
              />
            </svg>
            <div>
              {EXPENSE_TYPE_META.map((t) => (
                <Typography key={t.key} className={classes.textCardLable}>
                  <GoDotFill style={{ color: t.color, marginRight: 5 }} />
                  {t.label}: ₹{formatAmount(byType[t.key]?.amount || 0)} (
                  {byType[t.key]?.count || 0})
                </Typography>
              ))}
            </div>
          </div>
        </AccordionDetails>
      </Accordion>

      <Accordion
        expanded={expanded === "panel3"}
        onChange={handleChange("panel3")}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <b>Expense Details</b>
        </AccordionSummary>
        <AccordionDetails>
          <div className={classes.Reports_fields}>
            <Button
              variant="outlined"
              className={classesOne.filterbtns}
              onClick={(e) => setAnchorElFilter(e.currentTarget)}
              style={{ background: "white" }}
            >
              <div>
                <FilterIcon />{" "}
                {typeFilter === "all"
                  ? "All"
                  : EXPENSE_TYPE_META.find((t) => t.key === typeFilter)?.label}{" "}
                ({rows.length})
              </div>
              <div>
                <ArrowDownIcon />
              </div>
            </Button>
            <Menu
              anchorEl={anchorElFilter}
              open={Boolean(anchorElFilter)}
              onClose={() => setAnchorElFilter(null)}
            >
              <div className={classesOne.filterHeading}>
                <h5>Filter</h5>
                <span
                  className={classesOne.claerBtn}
                  onClick={() => {
                    setTypeFilter("all");
                    setAnchorElFilter(null);
                  }}
                >
                  clear all
                </span>
              </div>
              {EXPENSE_TYPE_META.map((t) => (
                <MenuItem
                  key={t.key}
                  style={{ padding: "6px 25px 6px 0", textTransform: "capitalize" }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={typeFilter === t.key}
                        onChange={() => {
                          setTypeFilter(t.key);
                          setAnchorElFilter(null);
                        }}
                        size="small"
                      />
                    }
                    label={t.label}
                  />
                </MenuItem>
              ))}
            </Menu>
          </div>

          {rows.length === 0 ? (
            <Typography sx={{ mt: 2 }}>
              No expenses recorded in this period
            </Typography>
          ) : (
            <div className={classes.TaskContainer}>
              {rows.map((item, index) => {
                const meta =
                  EXPENSE_TYPE_META.find((t) => t.key === item.type) ||
                  EXPENSE_TYPE_META[EXPENSE_TYPE_META.length - 1];
                const billUrl = resolveBillUrl(item.bill);
                return (
                  <Paper className={classes.TaskCard} key={item._id || index}>
                    <div className={classes.bottom_box}>
                      <Typography sx={{ fontWeight: 600 }}>
                        ₹{formatAmount(item.amount)}
                      </Typography>
                      <Typography
                        sx={{
                          display: "inline-block",
                          padding: "2px 10px",
                          borderRadius: "10px",
                          fontSize: "12px",
                          fontWeight: 600,
                          background: `${meta.color}22`,
                          color: meta.color,
                        }}
                      >
                        {meta.label}
                      </Typography>
                    </div>
                    <Typography sx={{ fontSize: "13px", color: "#6b778c", mt: 1 }}>
                      {dayjs(item.expense_date).format("DD MMM YYYY")}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", mt: 0.5 }}>
                      {item.location_title || "No related task"}
                    </Typography>
                    {billUrl && (
                      <Typography sx={{ fontSize: "13px", mt: 1 }}>
                        <a
                          href={billUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Bill
                        </a>
                      </Typography>
                    )}
                  </Paper>
                );
              })}
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const EmployeeReportSection = ({ report, reportType, ln }) => {
  const [expanded, setExpanded] = React.useState("panel1");
  const [anchorElTwo, setAnchorElTwo] = React.useState(null);
  const [anchorElThree, setAnchorElThree] = React.useState(null);
  const [filteredDatas, setFilteredDatas] = React.useState([]);
  const [selectedValues, setSelectedValues] = useState([]);
  const [selectedSortValues, setSelectedSortValues] = useState([
    "Newest to Oldest",
  ]);
  const [pieChartData, setPieChartData] = useState();

  useEffect(() => {
    if (reportType === "task") {
      const data = [
        { x: `${report?.completed}%`, y: report?.completed },
        { x: `${report?.pending}%`, y: report?.pending },
        { x: `${report?.rejected}%`, y: report?.rejected },
      ];
      setPieChartData(data);
    } else {
      const data = [
        {
          x: `${report?.number_of_daysPresent}%`,
          y: report?.number_of_daysPresent,
        },
        {
          x: `${report?.number_of_daysAbsent}%`,
          y: report?.number_of_daysAbsent,
        },
        {
          x: `${report?.number_of_daysholiday}%`,
          y: report?.number_of_daysholiday,
        },
      ];
      setPieChartData(data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, reportType]);

  const handleStatusCheckbox = (value) => {
    setSelectedValues([value]);
    let arrays;
    if (reportType === "task") {
      arrays = [...(report?.list || [])];
    } else {
      arrays = [...(report?.daysReport || [])];
    }
    if (arrays) {
      if (value === "all") {
        setFilteredDatas(arrays);
      } else {
        let _filters = arrays.filter(
          (val) => val.status.toLowerCase() === value?.toLowerCase()
        );
        setFilteredDatas(_filters);
      }
    }
  };

  const handleSortStatusCheckbox = (value) => {
    setSelectedSortValues([value]);

    let arrays = [...filteredDatas];
    let _fields = "";
    if (reportType === "task") {
      _fields = "createdAt";
    } else {
      _fields = "date";
    }
    if (arrays) {
      if (value === "Newest to Oldest") {
        let date_sortings = arrays.sort((a, b) => {
          return b[_fields].localeCompare(a[_fields]);
        });
        setFilteredDatas(date_sortings);
      } else if (value === "Oldest to Newest") {
        let date_sortings = arrays.sort((a, b) => {
          return a[_fields].localeCompare(b[_fields]);
        });
        setFilteredDatas(date_sortings);
      } else if (value === "all") {
        let date_sortings = arrays.sort((a, b) => {
          return a[_fields].localeCompare(b[_fields]);
        });
        setFilteredDatas(date_sortings);
      }
    }
  };

  const handleChange = (panel) => (event, newExpanded) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleMenuclick = (event) => {
    setAnchorElTwo(event.currentTarget);
  };

  const handleSortMenuclick = (event) => {
    setAnchorElThree(event.currentTarget);
  };

  useEffect(() => {
    if (report?.daysReport || report?.list) {
      handleStatusCheckbox("all");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  return (
    <div className={classes.AccordiansContainer}>
      <Accordion
        expanded={expanded === "panel1"}
        onChange={handleChange("panel1")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel1-content"
          id="panel1-header"
        >
          <b>{reportType === "task" ? "Tasks" : "Attendance"} Summary</b>
        </AccordionSummary>
        <AccordionDetails>
          <div className={classes.TextNumberCardContainer}>
            <div className={classes.TextNumberCard}>
              <Typography className={classes.textCardLable}>
                {reportType === "task" ? "Total Tasks" : "Total Days"}
              </Typography>
              <Typography className={classes.textCardLable}>
                {reportType === "task" ? report?.total : report?.workingDays}
              </Typography>
            </div>
            <div className={classes.TextNumberCard}>
              <Typography
                className={`${classes.textCardLable} ${classes.completedcolor}`}
              >
                <GoDotFill />
                {reportType !== "task" ? "Present" : "Completed"}
              </Typography>
              <Typography>
                {reportType !== "task"
                  ? report?.number_of_daysPresent
                  : report?.completed}
              </Typography>
            </div>
            <div className={classes.TextNumberCard}>
              <Typography
                className={`${classes.textCardLable} ${classes.pendingcolor}`}
              >
                <GoDotFill />
                {reportType !== "task" ? "Absent" : "Pending"}
              </Typography>
              <Typography>
                {reportType !== "task"
                  ? report?.number_of_daysAbsent
                  : report?.pending}
              </Typography>
            </div>
            <div className={classes.TextNumberCard}>
              <Typography
                className={`${classes.textCardLable} ${classes.rejectedcolor}`}
              >
                <GoDotFill />
                {reportType !== "task" ? "Holidays" : "Rejected"}
              </Typography>
              <Typography>
                {reportType !== "task"
                  ? report?.number_of_daysholiday
                  : report?.rejected}
              </Typography>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel2"}
        onChange={handleChange("panel2")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel2-content"
          id="panel2-header"
        >
          <b>
            {" "}
            {reportType === "task" ? "Overall Tasks" : "Overall Attendance"}{" "}
            Distribution
          </b>
        </AccordionSummary>
        <AccordionDetails>
          <div className={classes.pieChartContainer}>
            <svg viewBox="0 0 450 450" width="170px">
              <VictoryPie
                standalone={false}
                width={450}
                height={450}
                data={pieChartData}
                innerRadius={125}
                colorScale={["#240051", "#fbc23f", "#C52A38"]}
                labels={() => null} // Hide labels
                style={{ labels: { fontSize: 20, fill: "white" } }}
              />
              <VictoryLabel
                textAnchor="middle"
                verticalAnchor="middle"
                x={225}
                y={250}
                text={[
                  reportType === "task"
                    ? report?.total + ""
                    : report?.workingDays + "",
                  "Total",
                ]}
                style={[
                  { fontSize: 65, fontWeight: 600 },
                  {
                    fontSize: "30px",
                    fontWeight: 400,
                    fill: "#6B778C",
                  },
                ]}
              />
            </svg>
            <div>
              <Typography
                className={`${classes.textCardLable} ${classes.completedcolor}`}
              >
                <GoDotFill />
                {reportType === "task"
                  ? `${report?.completed} completed`
                  : `${report?.number_of_daysPresent} Present`}
              </Typography>
              <Typography
                className={`${classes.textCardLable} ${classes.pendingcolor}`}
              >
                <GoDotFill />
                {reportType === "task"
                  ? `${report?.pending} pending`
                  : `${report?.number_of_daysAbsent} Absent`}
              </Typography>
              <Typography
                className={`${classes.textCardLable} ${classes.rejectedcolor}`}
              >
                <GoDotFill />
                {reportType === "task"
                  ? `${report?.rejected} rejected`
                  : `${report?.number_of_daysholiday} Holidays`}
              </Typography>
            </div>
          </div>
        </AccordionDetails>
      </Accordion>
      <Accordion
        expanded={expanded === "panel3"}
        onChange={handleChange("panel3")}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          aria-controls="panel3-content"
          id="panel3-header"
        >
          <b>
            {reportType === "task" ? "Tasks Information" : "Attendance Details"}
          </b>
        </AccordionSummary>
        <AccordionDetails>
          {reportType === "task" ? (
            <>
              <div className={classes.Reports_fields}>
                <div>
                  <Button
                    variant="outlined"
                    className={classesOne.filterbtns}
                    onClick={handleMenuclick}
                    style={{ background: "white" }}
                  >
                    <div>
                      <FilterIcon /> {selectedValues[0] ?? "All"} (
                      {filteredDatas && filteredDatas?.length})
                    </div>
                    <div>
                      <ArrowDownIcon />
                    </div>
                  </Button>
                  <Menu
                    anchorEl={anchorElTwo}
                    open={Boolean(anchorElTwo)}
                    onClose={() => setAnchorElTwo(null)}
                  >
                    <div className={classesOne.filterHeading}>
                      <h5>Filter</h5>
                      <span
                        className={classesOne.claerBtn}
                        onClick={() => handleStatusCheckbox("all")}
                      >
                        clear all
                      </span>
                    </div>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedValues.includes("completed")}
                            onChange={() => handleStatusCheckbox("completed")}
                            size="small"
                          />
                        }
                        label="Completed"
                      />
                    </MenuItem>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedValues.includes("pending")}
                            onChange={() => handleStatusCheckbox("pending")}
                            size="small"
                          />
                        }
                        label="Pending"
                      />
                    </MenuItem>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedValues.includes("rejected")}
                            onChange={() => handleStatusCheckbox("rejected")}
                            size="small"
                          />
                        }
                        label="Rejected"
                      />
                    </MenuItem>
                  </Menu>
                </div>
                <div>
                  <Button
                    variant="outlined"
                    className={classesOne.sortingbtns}
                    onClick={handleSortMenuclick}
                    style={{ background: "white" }}
                  >
                    <div>
                      <ReportFilterIcon />{" "}
                      {selectedSortValues && selectedSortValues[0]}
                    </div>
                    <div>
                      <ArrowDownIcon />
                    </div>
                  </Button>
                  <Menu
                    anchorEl={anchorElThree}
                    open={Boolean(anchorElThree)}
                    onClose={() => setAnchorElThree(null)}
                  >
                    <div className={classesOne.filterHeading}>
                      <h5>Filter</h5>
                      <span
                        className={classesOne.claerBtn}
                        onClick={() =>
                          handleSortStatusCheckbox("Oldest to Newest")
                        }
                      >
                        clear all
                      </span>
                    </div>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSortValues.includes(
                              "Newest to Oldest"
                            )}
                            onChange={() =>
                              handleSortStatusCheckbox("Newest to Oldest")
                            }
                            size="small"
                          />
                        }
                        label="Newest to Oldest"
                      />
                    </MenuItem>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSortValues.includes(
                              "Oldest to Newest"
                            )}
                            onChange={() =>
                              handleSortStatusCheckbox("Oldest to Newest")
                            }
                            size="small"
                          />
                        }
                        label="Oldest to Newest"
                      />
                    </MenuItem>
                  </Menu>
                </div>
              </div>
              {filteredDatas && filteredDatas?.length > 0 ? (
                <div className={classes.TaskContainer}>
                  {filteredDatas?.map((item, index) => {
                    return (
                      <Paper key={`report${index}`} className={classes.TaskCard}>
                        <Typography
                          sx={{
                            color: "black",
                            fontSize: "16px",
                            marginBottom: "12px",
                            fontWeight: "500",
                          }}
                        >
                          {item.Location?.title}
                        </Typography>
                        <Box
                          className={classes.bottom_box}
                          sx={{ alignItems: "flex-start !important", gap: "12px", mb: "12px" }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography mb={1} fontSize={"15px"}>
                              Landmark Location
                            </Typography>
                            <Typography
                              sx={{ color: "black", fontSize: "15px", wordBreak: "break-word" }}
                            >
                              {item.landmark_location || "-"}
                            </Typography>
                          </Box>
                          <Box sx={{ flexShrink: 0, textAlign: "right" }}>
                            <Typography mb={1} fontSize={"15px"}>
                              Task Completion Time
                            </Typography>
                            {/* Arrival at the task → completion; "-" when either is unknown. */}
                            <Typography sx={{ color: "black", fontSize: "15px" }}>
                              {item.completion_time || "-"}
                            </Typography>
                          </Box>
                        </Box>
                        <Box className={classes.bottom_box}>
                          <Box>
                            <Typography mb={1} fontSize={"15px"}>
                              Status
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: "15px",
                                color:
                                  item.status === "pending"
                                    ? "#836824"
                                    : item.status === "completed"
                                    ? "#1A6B44"
                                    : "#AF0808",
                                backgroundColor:
                                  item.status === "pending"
                                    ? "#F8F3E7"
                                    : item.status === "completed"
                                    ? "#E6F9F0"
                                    : "#FEE2E2",
                                padding: "3px 15px",
                                borderRadius: "14px",
                                textTransform: "capitalize",
                              }}
                            >
                              {item.status}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography mb={1} fontSize={"15px"}>
                              Assigned On:
                            </Typography>
                            <Typography sx={{ color: "black", fontSize: "15px" }}>
                              {moment(item.createdAt).format("DD-MMMM-YYYY")},
                              {moment(item.createdAt).format("hh:mm:ss A")}
                            </Typography>
                          </Box>
                        </Box>
                      </Paper>
                    );
                  })}
                </div>
              ) : null}
            </>
          ) : (
            <>
              <div className={classes.Reports_fields}>
                <div>
                  <Button
                    variant="outlined"
                    className={classesOne.filterbtns}
                    onClick={handleMenuclick}
                    style={{ background: "white" }}
                  >
                    <div>
                      <FilterIcon /> {selectedValues[0] ?? "All"} (
                      {filteredDatas && filteredDatas?.length})
                    </div>
                    <div>
                      <ArrowDownIcon />
                    </div>
                  </Button>
                  <Menu
                    anchorEl={anchorElTwo}
                    open={Boolean(anchorElTwo)}
                    onClose={() => setAnchorElTwo(null)}
                  >
                    <div className={classesOne.filterHeading}>
                      <h5>Filter</h5>
                      <span
                        className={classesOne.claerBtn}
                        onClick={() => handleStatusCheckbox("all")}
                      >
                        clear all
                      </span>
                    </div>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedValues.includes("Absent")}
                            onChange={() => handleStatusCheckbox("Absent")}
                            size="small"
                          />
                        }
                        label="Absent"
                      />
                    </MenuItem>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedValues.includes("Present")}
                            onChange={() => handleStatusCheckbox("Present")}
                            size="small"
                          />
                        }
                        label="Present"
                      />
                    </MenuItem>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedValues.includes("Holiday")}
                            onChange={() => handleStatusCheckbox("Holiday")}
                            size="small"
                          />
                        }
                        label="Holiday"
                      />
                    </MenuItem>
                  </Menu>
                </div>
                <div>
                  <Button
                    variant="outlined"
                    className={classesOne.sortingbtns}
                    onClick={handleSortMenuclick}
                    style={{ background: "white" }}
                  >
                    <div>
                      <ReportFilterIcon />{" "}
                      {selectedSortValues && selectedSortValues[0]}
                    </div>
                    <div>
                      <ArrowDownIcon />
                    </div>
                  </Button>
                  <Menu
                    anchorEl={anchorElThree}
                    open={Boolean(anchorElThree)}
                    onClose={() => setAnchorElThree(null)}
                  >
                    <div className={classesOne.filterHeading}>
                      <h5>Filter</h5>
                      <span
                        className={classesOne.claerBtn}
                        onClick={() =>
                          handleSortStatusCheckbox("Newest to Oldest")
                        }
                      >
                        clear all
                      </span>
                    </div>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSortValues.includes(
                              "Newest to Oldest"
                            )}
                            onChange={() =>
                              handleSortStatusCheckbox("Newest to Oldest")
                            }
                            size="small"
                          />
                        }
                        label="Newest to Oldest"
                      />
                    </MenuItem>
                    <MenuItem
                      style={{
                        padding: "6px 25px 6px 0",
                        textTransform: "capitalize",
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedSortValues.includes(
                              "Oldest to Newest"
                            )}
                            onChange={() =>
                              handleSortStatusCheckbox("Oldest to Newest")
                            }
                            size="small"
                          />
                        }
                        label="Oldest to Newest"
                      />
                    </MenuItem>
                  </Menu>
                </div>
              </div>
              {filteredDatas && filteredDatas?.length > 0 ? (
                <div className={classesOne.AttendanceContainer}>
                  {filteredDatas?.map((item, index) => {
                    return (
                      <Paper
                        key={`report${index}`}
                        className={classesOne.AttendanceCard}
                      >
                        <div className={classesOne.attendanceCardName}>
                          <p>{moment(item.date).format("D")}</p>
                          <p>{moment(item.date).format("MMM")}</p>
                        </div>
                        <div className={classesOne.attendanceCardName2}>
                          <Typography
                            style={{
                              display: "flex",
                              textTransform: "capitalize",
                            }}
                            className={classesOne.step_label}
                          >
                            <div
                              style={{
                                background:
                                  item.status === "Present"
                                    ? "#22A06B"
                                    : "#EA3A3A",
                                width: "13px",
                                height: "13px",
                                borderRadius: "50%",
                                marginTop: "5px",
                                marginRight: "5px",
                              }}
                            ></div>{" "}
                            {item.status}
                          </Typography>
                          <Typography
                            className={classesOne.sub_step_label}
                            style={{ marginTop: "5px" }}
                          >
                            {ln.work_duration} : {item.duration}
                          </Typography>
                        </div>
                      </Paper>
                    );
                  })}
                </div>
              ) : (
                "No Data Found"
              )}
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
};

const ViewReport = () => {
  const [queryParams, setQueryParams] = useState({});
  const [reportData, setReportData] = useState(null);
  const location = useLocation();
  const query = useQuery();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const ln = useSelector((state) => state.allUsers.language_json);

  // The expense report sends ISO YYYY-MM-DD: the backend's IST date utilities
  // parse MM-DD-YYYY a day early. Attendance and Task keep the old format so
  // their existing (separately tracked) behaviour is unchanged.
  const rangeDateFormat =
    queryParams?.tp === "expense" ? "YYYY-MM-DD" : "MM-DD-YYYY";

  const handleDownload = async (value) => {
    // Close the PDF/Excel popover immediately so a second click can't queue
    // another download while the first one is still in flight.
    handleClose();

    if (downloading) return;

    setDownloading(true);
    try {
      const { data } = await authApi.get(
        `report/${queryParams.tp}-report/download/${queryParams.mb}/${
          queryParams?.id
        }?startDate=${dayjs(queryParams.sd).format(
          rangeDateFormat
        )}&endDate=${dayjs(queryParams.ed).format(
          rangeDateFormat
        )}&type=${value}`,
        {
          headers: { Authorization: getAuthorizationHeader() },
          responseType: "blob",
        }
      );

      const mimeType =
        value === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([data], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");
      a.href = blobUrl;
      a.download =
        value === "pdf"
          ? `${queryParams.tp}Report.pdf`
          : `${queryParams.tp}Report.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
      toast.success("Report downloaded successfully");
    } catch (error) {
      console.log(error);
      toast.error(await getDownloadErrorMessage(error));
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    const params = {};
    for (let [key, value] of query.entries()) {
      params[key] = value;
    }
    setQueryParams(params);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const getReportDatas = async () => {
    try {
      setLoading(true);
      const { data } = await authApi.get(
        `report/${queryParams?.tp}-report/download/${queryParams?.mb}/${
          queryParams?.id
        }?startDate=${dayjs(queryParams?.sd).format(
          rangeDateFormat
        )}&endDate=${dayjs(queryParams?.ed).format(rangeDateFormat)}&type=`,
        {
          headers: { Authorization: getAuthorizationHeader() },
        }
      );
      setReportData(data);
      setLoading(false);
    } catch (error) {
      setLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    if (Object.keys(queryParams)?.length > 0) {
      getReportDatas();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParams]);

  const reports = Array.isArray(reportData)
    ? reportData
    : reportData
    ? [reportData]
    : [];
  const isMulti = reports.length > 1;

  return (
    <div>
      {loading ? (
        'Loading...'
      ) : (
        <>
          <div className={classes.downloadBtnContainer}>
            <Button
              className={`${classes.viewReportBtn} ${classes.downloadFlexBtn}`}
              variant="outlined"
              disabled={downloading}
              id="basic-button"
              aria-controls={open ? "basic-menu" : undefined}
              aria-haspopup="true"
              aria-expanded={open ? "true" : undefined}
              onClick={handleClick}
            >
              {downloading ? (
                <>
                  <CircularProgress size={16} color="inherit" style={{ marginRight: 8 }} />
                  <span>Downloading...</span>
                </>
              ) : (
                <>
                  <span>Download Report</span>
                  <ArrowDownIcon />
                </>
              )}
            </Button>
            <StyledMenu
              id="basic-menu"
              anchorEl={anchorEl}
              open={open}
              onClose={handleClose}
              MenuListProps={{
                "aria-labelledby": "basic-button",
              }}
              sx={{
                width: "100%",
              }}
            >
              <MenuItem onClick={() => handleDownload("pdf")}>
                <PdfIcon /> PDF
              </MenuItem>
              <MenuItem onClick={() => handleDownload("excel")}>
                <ExcelIcon /> Excel
              </MenuItem>
            </StyledMenu>
          </div>
          {reports.length === 0 ? (
            <Typography sx={{ mt: 2 }}>No Data Found</Typography>
          ) : (
            reports.map((report, index) => (
              <div key={report?.member_details?._id || index}>
                {isMulti && (
                  <Typography
                    variant="h6"
                    sx={{ mt: index === 0 ? 0 : 3, mb: 1, fontWeight: 600 }}
                  >
                    {report?.member_details?.user_name || `Employee ${index + 1}`}
                  </Typography>
                )}
                {queryParams?.tp === "expense" ? (
                  <ExpenseReportSection report={report} />
                ) : (
                  <EmployeeReportSection
                    report={report}
                    reportType={queryParams?.tp}
                    ln={ln}
                  />
                )}
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
};

export default ViewReport;
